import GeoJSON from 'ol/format/GeoJSON';
import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import proj4 from 'proj4';
import { openShp, read as readShapefile } from 'shapefile';
import JSZip from 'jszip';
import type { AttributeValue, OriginalAttributes, VectorFeatureRecord, VectorLayerModel } from '../core/types';
import { createGenericLayer } from '../domain/layers';

export type ManualShapefileCrs = 'EPSG:4326' | 'EPSG:3857' | 'EPSG:32616';
export const MANUAL_SHAPEFILE_CRS: readonly { readonly value: ManualShapefileCrs; readonly label: string }[] = [
  { value: 'EPSG:4326', label: 'EPSG:4326 — WGS 84 (longitud/latitud)' },
  { value: 'EPSG:3857', label: 'EPSG:3857 — Web Mercator' },
  { value: 'EPSG:32616', label: 'EPSG:32616 — WGS 84 / UTM zona 16N' },
];
export const isManualShapefileCrs = (value: string): value is ManualShapefileCrs => MANUAL_SHAPEFILE_CRS.some((option) => option.value === value);
type ComponentExtension = 'shp' | 'dbf' | 'shx' | 'prj' | 'cpg';

export interface ShapefileImportOptions {
  /** One CRS for every dataset without a usable .prj (explicit caller decision, e.g. the CASUR hydric reference). */
  readonly fallbackCrs?: ManualShapefileCrs;
  /** CRS confirmed by the user per dataset key (see ShapefileCrsRequiredError.datasets). Takes precedence over fallbackCrs. */
  readonly crsByDataset?: Readonly<Record<string, ManualShapefileCrs>>;
}

export interface ShapefileComponentGroup {
  readonly key: string;
  readonly name: string;
  readonly components: Readonly<Partial<Record<ComponentExtension, string>>>;
}

/** Components that could not be grouped: auxiliaries without a .shp and repeated components of one dataset. */
export interface ShapefileComponentClassification {
  readonly groups: readonly ShapefileComponentGroup[];
  readonly orphans: readonly string[];
  readonly duplicates: readonly string[];
}

interface BinaryComponentGroup {
  readonly key: string;
  readonly name: string;
  readonly sourceName: string;
  readonly components: Readonly<Partial<Record<ComponentExtension, ArrayBuffer>>>;
}

export interface CrsPendingDataset { readonly key: string; readonly name: string; readonly reason: 'missing' | 'invalid' }

export class ShapefileCrsRequiredError extends Error {
  readonly datasetNames: readonly string[];
  readonly reason: 'missing' | 'invalid';
  constructor(readonly datasets: readonly CrsPendingDataset[]) {
    const names = datasets.map(({ name }) => name);
    const reason = datasets.some((dataset) => dataset.reason === 'invalid') ? 'invalid' : 'missing';
    super(reason === 'missing'
      ? `Selecciona el CRS de origen antes de cargar: ${names.join(', ')}.`
      : `No se pudo interpretar el CRS de ${names.join(', ')}. Selecciona el CRS de origen.`);
    this.name = 'ShapefileCrsRequiredError';
    this.datasetNames = names; this.reason = reason;
  }
}

export class ShapefileImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ShapefileImportError';
  }
}

/** What the import found for one dataset: shown before the layer is added, never inferred later. */
export interface ShapefileDatasetSummary {
  readonly key: string;
  readonly name: string;
  readonly sourceName: string;
  readonly shapeType: string;
  readonly geometryLabel: string;
  readonly recordCount: number;
  readonly validCount: number;
  readonly nullCount: number;
  readonly rejectedCount: number;
  readonly sourceCrs: string;
  readonly crsOrigin: 'prj' | 'manual';
  readonly hasDbf: boolean;
  readonly fieldCount: number;
  readonly encoding: string | null;
  readonly hasShx: boolean;
  /** «valid»: index coherent with the .shp records; «inconsistent»: present but ignored (see warnings). */
  readonly shxStatus: 'valid' | 'missing' | 'inconsistent';
  readonly warnings: readonly string[];
}
export interface ShapefileDataset { readonly layer: VectorLayerModel; readonly summary: ShapefileDatasetSummary }
export interface ShapefileInspection { readonly datasets: readonly ShapefileDataset[]; readonly notices: readonly string[] }

const componentPattern = /^(.*)\.(shp|dbf|shx|prj|cpg)$/i;
const normalizePath = (name: string): string => name.replace(/\\/g, '/').replace(/^\.\//, '');
const basename = (name: string): string => normalizePath(name).split('/').pop() ?? name;
const ignoredPath = (name: string): boolean => name.startsWith('__MACOSX/') || basename(name).startsWith('.');

/**
 * Groups components by their full path without extension (case-insensitive), so «Hidrantes.SHP» + «hidrantes.dbf» are
 * one dataset while «norte/Hidrantes.shp» and «sur/Hidrantes.shp» stay two. Never pairs a .dbf with another base name.
 */
export function classifyShapefileComponents(names: readonly string[]): ShapefileComponentClassification {
  const groups = new Map<string, { name: string; path: string; components: Partial<Record<ComponentExtension, string>> }>();
  const duplicates: string[] = [];
  names.forEach((rawName) => {
    const name = normalizePath(rawName);
    if (ignoredPath(name)) return;
    const match = name.match(componentPattern);
    if (!match?.[1] || !match[2]) return;
    const extension = match[2].toLowerCase() as ComponentExtension;
    const key = match[1].toLocaleLowerCase('en');
    const group = groups.get(key) ?? { name: basename(match[1]), path: match[1], components: {} };
    if (extension in group.components) duplicates.push(rawName);
    else group.components[extension] = rawName;
    groups.set(key, group);
  });
  const withShp = [...groups].filter(([, group]) => Boolean(group.components.shp));
  const repeated = new Set(withShp.map(([, group]) => group.name.toLocaleLowerCase('en')).filter((name, index, all) => all.indexOf(name) !== index));
  return {
    groups: withShp.map(([key, group]) => ({ key, name: repeated.has(group.name.toLocaleLowerCase('en')) ? group.path : group.name, components: group.components })),
    orphans: [...groups.values()].filter((group) => !group.components.shp).flatMap((group) => Object.values(group.components)),
    duplicates,
  };
}

export function groupShapefileComponentNames(names: readonly string[]): ShapefileComponentGroup[] {
  return [...classifyShapefileComponents(names).groups];
}

const toArrayBuffer = (bytes: Uint8Array): ArrayBuffer => bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
interface CollectedGroups { readonly groups: BinaryComponentGroup[]; readonly notices: string[] }

const classificationNotices = (classification: ShapefileComponentClassification, source: string): string[] => {
  if (classification.duplicates.length) throw new ShapefileImportError(`${source}: hay componentes repetidos para el mismo dataset (${classification.duplicates.map(basename).join(', ')}). Deja solo uno por extensión.`);
  return classification.orphans.length ? [`${source}: ${classification.orphans.map(basename).join(', ')} no tiene un .shp con el mismo nombre; no se usó.`] : [];
};

async function groupsFromZip(file: File): Promise<CollectedGroups> {
  let zip: JSZip;
  try { zip = await JSZip.loadAsync(await file.arrayBuffer()); } catch { throw new ShapefileImportError(`El ZIP «${file.name}» está dañado o no es un ZIP válido.`); }
  const entries = Object.values(zip.files).filter((entry) => !entry.dir);
  const classification = classifyShapefileComponents(entries.map((entry) => entry.name));
  if (!classification.groups.length) throw new ShapefileImportError(`El ZIP «${file.name}» no contiene ningún .shp.`);
  const notices = classificationNotices(classification, file.name);
  const groups = await Promise.all(classification.groups.map(async (group) => {
    const components: Partial<Record<ComponentExtension, ArrayBuffer>> = {};
    for (const [extension, entryName] of Object.entries(group.components) as [ComponentExtension, string][]) {
      const entry = zip.file(entryName);
      if (!entry) continue;
      try { components[extension] = toArrayBuffer(await entry.async('uint8array')); } catch { throw new ShapefileImportError(`No se pudo extraer ${basename(entryName)} del ZIP «${file.name}»: el archivo está dañado.`); }
    }
    return { key: `${file.name.toLocaleLowerCase('en')}:${group.key}`, name: group.name, sourceName: `${file.name} · ${group.name}`, components };
  }));
  return { groups, notices };
}

async function groupsFromLooseFiles(files: readonly File[]): Promise<CollectedGroups> {
  if (!files.length) return { groups: [], notices: [] };
  const classification = classifyShapefileComponents(files.map((file) => file.name));
  const notices = classificationNotices(classification, 'Archivos seleccionados');
  const byName = new Map(files.map((file) => [file.name.toLocaleLowerCase('en'), file]));
  const groups = await Promise.all(classification.groups.map(async (group) => {
    const components: Partial<Record<ComponentExtension, ArrayBuffer>> = {};
    for (const [extension, name] of Object.entries(group.components) as [ComponentExtension, string][]) {
      const file = byName.get(name.toLocaleLowerCase('en'));
      if (file) components[extension] = await file.arrayBuffer();
    }
    return { key: group.key, name: group.name, sourceName: basename(group.components.shp ?? group.name), components };
  }));
  return { groups, notices };
}

async function collectGroups(files: readonly File[]): Promise<CollectedGroups> {
  const zipFiles = files.filter((file) => file.name.toLowerCase().endsWith('.zip'));
  const looseFiles = files.filter((file) => !file.name.toLowerCase().endsWith('.zip'));
  const collected = [...await Promise.all(zipFiles.map(groupsFromZip)), await groupsFromLooseFiles(looseFiles)];
  const groups = collected.flatMap(({ groups: items }) => items);
  if (!groups.length) throw new ShapefileImportError('Selecciona un .shp, sus componentes (.shp, .dbf, .shx, .prj, .cpg) o un ZIP que los contenga.');
  return { groups, notices: collected.flatMap(({ notices }) => notices) };
}

proj4.defs('EPSG:32616', '+proj=utm +zone=16 +datum=WGS84 +units=m +no_defs +type=crs');

const decodeText = (buffer: ArrayBuffer): string => new TextDecoder('utf-8').decode(buffer).replace(/^﻿/, '').trim();
const detectEpsg = (prj: string): string | null => {
  const authority = [...prj.matchAll(/AUTHORITY\s*\[\s*["']EPSG["']\s*,\s*["']?(\d+)["']?\s*\]/gi)].at(-1)?.[1];
  if (authority) return `EPSG:${authority}`;
  if (/WGS[_ ]?1984.*UTM[_ ]?Zone[_ ]?16N|UTM[^\]]*16N/i.test(prj)) return 'EPSG:32616';
  if (/Web[_ ]Mercator|Pseudo[_ -]?Mercator|Auxiliary_Sphere/i.test(prj)) return 'EPSG:3857';
  if (/GEOGCS\s*\[\s*["']WGS\s*84|GCS_WGS_1984/i.test(prj)) return 'EPSG:4326';
  return null;
};

interface Projection { readonly definition: string; readonly sourceCrs: string; readonly assumed: boolean; readonly notice?: string }

/** A converter is accepted only if it projects a sample point to finite EPSG:3857 coordinates. */
const usable = (definition: string, sample: [number, number]): boolean => {
  try { return proj4(definition, 'EPSG:3857').forward(sample).every(Number.isFinite); } catch { return false; }
};

const resolveProjection = (group: BinaryComponentGroup, options: ShapefileImportOptions): Projection => {
  const manual = options.crsByDataset?.[group.key] ?? options.fallbackCrs;
  const prj = group.components.prj ? decodeText(group.components.prj) : '';
  if (!prj) {
    if (!manual) throw new ShapefileCrsRequiredError([{ key: group.key, name: group.name, reason: 'missing' }]);
    return { definition: manual, sourceCrs: manual, assumed: true };
  }
  const detected = detectEpsg(prj);
  const sample: [number, number] = detected === 'EPSG:4326' ? [0, 0] : [500000, 1300000];
  // An EPSG code proj4 does not know is still usable through the WKT itself; the code is kept as the label.
  const definition = detected && usable(detected, sample) ? detected : usable(prj, sample) ? prj : null;
  if (definition) return { definition, sourceCrs: detected ?? prj, assumed: false };
  if (!manual) throw new ShapefileCrsRequiredError([{ key: group.key, name: group.name, reason: 'invalid' }]);
  return { definition: manual, sourceCrs: manual, assumed: true, notice: `${group.name}: el .prj no se pudo interpretar; se usó el CRS confirmado ${manual}.` };
};

/** .cpg → TextDecoder label. Without .cpg the shapefile convention (windows-1252) is used, and said so. */
const encodingFromCpg = (buffer: ArrayBuffer | undefined, name: string): { readonly encoding: string; readonly notice: string | null } => {
  if (!buffer) return { encoding: 'windows-1252', notice: `${name}: no se incluyó .cpg; los atributos se leyeron como windows-1252.` };
  const raw = decodeText(buffer); const value = raw.toLowerCase().replace(/[_\s]/g, '-');
  const label = value === '65001' || value === 'utf8' ? 'utf-8' : value === '1252' || value === 'ansi-1252' ? 'windows-1252' : value === '88591' || value === '8859-1' ? 'iso-8859-1' : value;
  try { new TextDecoder(label); return { encoding: label, notice: null }; } catch {
    return { encoding: 'windows-1252', notice: `${name}: el .cpg indica «${raw}», que no se reconoce; los atributos se leyeron como windows-1252.` };
  }
};

const SHAPE_TYPES: Readonly<Record<number, { readonly name: string; readonly label: string; readonly zm: boolean }>> = {
  0: { name: 'Null', label: 'Sin geometría', zm: false },
  1: { name: 'Point', label: 'Puntos', zm: false }, 3: { name: 'PolyLine', label: 'Líneas', zm: false },
  5: { name: 'Polygon', label: 'Polígonos', zm: false }, 8: { name: 'MultiPoint', label: 'Multipuntos', zm: false },
  11: { name: 'PointZ', label: 'Puntos', zm: true }, 13: { name: 'PolyLineZ', label: 'Líneas', zm: true },
  15: { name: 'PolygonZ', label: 'Polígonos', zm: true }, 18: { name: 'MultiPointZ', label: 'Multipuntos', zm: true },
  21: { name: 'PointM', label: 'Puntos', zm: true }, 23: { name: 'PolyLineM', label: 'Líneas', zm: true },
  25: { name: 'PolygonM', label: 'Polígonos', zm: true }, 28: { name: 'MultiPointM', label: 'Multipuntos', zm: true },
};

/** Physical position of each record in the .shp, in 16-bit words as the .shx stores them (offset of the record header, content length). */
interface ShpRecordPosition { readonly offset: number; readonly length: number }
interface ShpStructure { readonly shapeType: number; readonly records: number; readonly nullRecords: readonly number[]; readonly positions: readonly ShpRecordPosition[]; readonly notices: readonly string[] }

/**
 * Structural check of the .shp before reading it. The reader library silently scans past damaged records, so
 * anything that would make it skip or misread a record is rejected here with the record number.
 */
export function inspectShpStructure(buffer: ArrayBuffer, name: string): ShpStructure {
  if (buffer.byteLength < 100) throw new ShapefileImportError(`${name}: el .shp está incompleto (falta la cabecera de 100 bytes).`);
  const view = new DataView(buffer);
  if (view.getInt32(0, false) !== 9994) throw new ShapefileImportError(`${name}: el archivo no es un .shp válido (firma 9994 ausente).`);
  if (view.getInt32(28, true) !== 1000) throw new ShapefileImportError(`${name}: versión de Shapefile ${view.getInt32(28, true)} no admitida (se esperaba 1000).`);
  const declared = view.getInt32(24, false) * 2;
  if (declared > buffer.byteLength) throw new ShapefileImportError(`${name}: el .shp está truncado (declara ${declared.toLocaleString('es-NI')} bytes y contiene ${buffer.byteLength.toLocaleString('es-NI')}).`);
  const shapeType = view.getInt32(32, true);
  if (shapeType === 31) throw new ShapefileImportError(`${name}: el tipo MultiPatch (31) no es compatible con GeoStudio.`);
  if (!(shapeType in SHAPE_TYPES)) throw new ShapefileImportError(`${name}: tipo de geometría ${shapeType} no admitido.`);
  const notices: string[] = []; if (declared < buffer.byteLength) notices.push(`${name}: el .shp tiene ${buffer.byteLength - declared} bytes después del final declarado; se ignoraron.`);
  const nullRecords: number[] = []; const positions: ShpRecordPosition[] = []; let records = 0; let sequence = true;
  for (let offset = 100; offset < declared;) {
    const record = records + 1;
    if (offset + 12 > declared) throw new ShapefileImportError(`${name}: el registro ${record} del .shp está incompleto.`);
    const length = view.getInt32(offset + 4, false) * 2;
    if (length < 4 || offset + 8 + length > declared) throw new ShapefileImportError(`${name}: el registro ${record} del .shp está dañado (longitud ${length} bytes no válida).`);
    const type = view.getInt32(offset + 8, true);
    if (type === 0) nullRecords.push(record);
    else if (type !== shapeType) throw new ShapefileImportError(`${name}: el registro ${record} es de tipo ${type}, distinto del tipo ${shapeType} del archivo.`);
    if (view.getInt32(offset, false) !== record) sequence = false;
    positions.push({ offset: offset / 2, length: length / 2 });
    records += 1; offset += 8 + length;
  }
  if (!sequence) notices.push(`${name}: la numeración de registros del .shp no es correlativa; se usó el orden del archivo.`);
  return { shapeType, records, nullRecords, positions, notices };
}

/** .dbf header check: complete and with exactly one row per .shp record (otherwise it is not the same dataset). */
const checkDbf = (buffer: ArrayBuffer, name: string, shpRecords: number): void => {
  if (buffer.byteLength < 33) throw new ShapefileImportError(`${name}: el .dbf está incompleto.`);
  const view = new DataView(buffer);
  const count = view.getUint32(4, true); const headerLength = view.getUint16(8, true); const recordLength = view.getUint16(10, true);
  if (headerLength < 33 || recordLength < 1 || headerLength + count * recordLength > buffer.byteLength) throw new ShapefileImportError(`${name}: el .dbf está dañado o truncado.`);
  if (count !== shpRecords) throw new ShapefileImportError(`${name}: el .dbf tiene ${count.toLocaleString('es-NI')} registros y el .shp ${shpRecords.toLocaleString('es-NI')}. No corresponden al mismo dataset o uno está incompleto.`);
};

/**
 * .shx check against the physical records found in the .shp: header (signature, version, declared length, shape
 * type) and every entry (offset and content length, in 16-bit words). GeoStudio reads the .shp sequentially and
 * only after validating its structure, so an inconsistent index does not block the import, but it is never accepted
 * silently: the problems are returned and reported, and the index is ignored. Empty list = coherent index.
 */
export function shxProblems(buffer: ArrayBuffer, structure: Pick<ShpStructure, 'shapeType' | 'records' | 'positions'>): string[] {
  if (buffer.byteLength < 100) return [`cabecera incompleta (${buffer.byteLength} bytes)`];
  const view = new DataView(buffer); const problems: string[] = [];
  if (view.getInt32(0, false) !== 9994) return ['firma 9994 ausente'];
  if (view.getInt32(28, true) !== 1000) problems.push(`versión ${view.getInt32(28, true)} (se esperaba 1000)`);
  const declared = view.getInt32(24, false) * 2;
  if (declared > buffer.byteLength) problems.push(`truncado: declara ${declared} bytes y contiene ${buffer.byteLength}`);
  else if (declared < buffer.byteLength) problems.push(`${buffer.byteLength - declared} bytes después del final declarado`);
  const type = view.getInt32(32, true);
  if (type !== structure.shapeType) problems.push(`tipo geométrico ${type}, distinto del tipo ${structure.shapeType} del .shp`);
  const usable = Math.min(declared, buffer.byteLength) - 100;
  if (usable % 8 !== 0) problems.push('última entrada incompleta');
  const entries = Math.max(0, Math.floor(usable / 8));
  if (entries !== structure.records) problems.push(`indexa ${entries} registros y el .shp contiene ${structure.records}`);
  const wrongOffsets: number[] = []; const wrongLengths: number[] = [];
  for (let index = 0; index < Math.min(entries, structure.records); index += 1) {
    const expected = structure.positions[index]!;
    if (view.getInt32(100 + index * 8, false) !== expected.offset) wrongOffsets.push(index + 1);
    if (view.getInt32(104 + index * 8, false) !== expected.length) wrongLengths.push(index + 1);
  }
  const list = (records: readonly number[]): string => `${records.slice(0, 8).join(', ')}${records.length > 8 ? ` y ${records.length - 8} más` : ''}`;
  if (wrongOffsets.length) problems.push(`desplazamiento incorrecto en ${wrongOffsets.length} registro(s) (${list(wrongOffsets)})`);
  if (wrongLengths.length) problems.push(`longitud incorrecta en ${wrongLengths.length} registro(s) (${list(wrongLengths)})`);
  return problems;
}

type Coordinates = number[] | Coordinates[];
interface MutableGeoJsonGeometry { type: string; coordinates?: Coordinates; geometries?: MutableGeoJsonGeometry[] }

const transformCoordinates = (coordinates: Coordinates, forward: (coordinate: [number, number]) => [number, number]): Coordinates => {
  if (typeof coordinates[0] === 'number') {
    const position = coordinates as number[];
    const projected = forward([position[0]!, position[1]!]);
    if (!projected.every(Number.isFinite)) throw new ShapefileImportError('La reproyección produjo coordenadas no válidas. Verifica el CRS de origen.');
    return [projected[0], projected[1], ...position.slice(2)];
  }
  return (coordinates as Coordinates[]).map((part) => transformCoordinates(part, forward));
};

const transformGeometry = (geometry: MutableGeoJsonGeometry, forward: (coordinate: [number, number]) => [number, number]): MutableGeoJsonGeometry =>
  geometry.coordinates ? { ...geometry, coordinates: transformCoordinates(geometry.coordinates, forward) } : geometry;

const positions = (coordinates: Coordinates | undefined): number[][] => !coordinates ? [] : typeof coordinates[0] === 'number' ? [coordinates as number[]] : (coordinates as Coordinates[]).flatMap(positions);
/** Why a decoded record cannot become a map object (null when it can). Nothing is repaired or simplified. */
const geometryProblem = (geometry: MutableGeoJsonGeometry): string | null => {
  const points = positions(geometry.coordinates);
  if (!points.length) return 'sin vértices';
  if (points.some((point) => !Number.isFinite(point[0]) || !Number.isFinite(point[1]))) return 'coordenadas no finitas';
  const parts = geometry.coordinates as Coordinates[];
  if (geometry.type === 'LineString' && parts.length < 2) return 'línea con menos de 2 vértices';
  if (geometry.type === 'MultiLineString' && (parts as number[][][]).some((line) => line.length < 2)) return 'línea con menos de 2 vértices';
  if (geometry.type === 'Polygon' && (parts as number[][][]).some((ring) => ring.length < 4)) return 'anillo con menos de 4 vértices';
  if (geometry.type === 'MultiPolygon' && (parts as number[][][][]).some((polygon) => polygon.some((ring) => ring.length < 4))) return 'anillo con menos de 4 vértices';
  return null;
};
/** Geographic coordinates must be within ±180/±90 for the CRS the user confirmed; nothing is guessed from ranges. */
const outOfGeographicRange = (geometry: MutableGeoJsonGeometry): boolean => positions(geometry.coordinates).some(([x, y]) => Math.abs(x!) > 180 || Math.abs(y!) > 90);

const normalizeValue = (value: unknown): AttributeValue => value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? value : value === undefined ? null : JSON.stringify(value);
let importSequence = 0;
const slug = (value: string): string => value.normalize('NFKD').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'shapefile';
const listRecords = (records: readonly number[]): string => `${records.slice(0, 12).join(', ')}${records.length > 12 ? ` y ${records.length - 12} más` : ''}`;

interface DecodedRecord { readonly geometry: MutableGeoJsonGeometry | null; readonly properties: Record<string, unknown> }

/**
 * Path A (.shp + .dbf): geometry and attributes. Path B (.shp alone): geometry only, through the library's openShp.
 * Buffers are passed as Uint8Array views: the library type-checks with instanceof, which fails for an ArrayBuffer from
 * another realm (worker, test environment) and would otherwise be misread as a stream.
 */
async function decodeRecords(group: BinaryComponentGroup, shpBuffer: ArrayBuffer, encoding: string): Promise<DecodedRecord[]> {
  const shp = new Uint8Array(shpBuffer); const dbf = group.components.dbf ? new Uint8Array(group.components.dbf) : undefined;
  try {
    if (dbf) {
      const collection = await readShapefile(shp, dbf, { encoding });
      return collection.features.map((feature) => ({ geometry: feature.geometry as MutableGeoJsonGeometry | null, properties: (feature.properties ?? {}) as Record<string, unknown> }));
    }
    const source = await openShp(shp);
    const records: DecodedRecord[] = [];
    for (let result = await source.read(); !result.done; result = await source.read()) records.push({ geometry: result.value as unknown as MutableGeoJsonGeometry | null, properties: {} });
    return records;
  } catch {
    throw new ShapefileImportError(`No se pudo leer ${group.name}. ${dbf ? 'Verifica que los archivos .shp y .dbf pertenezcan al mismo dataset y no estén dañados.' : 'Verifica que el .shp no esté dañado.'}`);
  }
}

async function parseGroup(group: BinaryComponentGroup, options: ShapefileImportOptions): Promise<ShapefileDataset> {
  const shp = group.components.shp;
  if (!shp) throw new ShapefileImportError(`Falta el archivo .shp para ${group.name}.`);
  const structure = inspectShpStructure(shp, group.name);
  const shapeInfo = SHAPE_TYPES[structure.shapeType]!;
  const notices: string[] = [...structure.notices];
  const dbf = group.components.dbf;
  if (dbf) checkDbf(dbf, group.name, structure.records);
  else notices.push(`${group.name}: el Shapefile no incluye un archivo DBF. Se importaron las geometrías disponibles, sin atributos adicionales.`);
  // The .shp structure is already validated above, so the .shx only informs: an inconsistent index is ignored, and said so.
  const shxIssues = group.components.shx ? shxProblems(group.components.shx, structure) : null;
  if (!group.components.shx) notices.push(`${group.name}: no se incluyó .shx; la geometría se leyó secuencialmente.`);
  else if (shxIssues?.length) notices.push(`${group.name}: el .shx no es coherente con el .shp (${shxIssues.join('; ')}). Se ignoró el índice y la geometría se leyó secuencialmente del .shp, cuya estructura se validó.`);
  const encoding = dbf ? encodingFromCpg(group.components.cpg, group.name) : null;
  if (encoding?.notice) notices.push(encoding.notice);
  if (shapeInfo.zm) notices.push(`${group.name}: el tipo ${shapeInfo.name} incluye valores Z/M; GeoStudio importa X/Y y no conserva Z/M.`);
  const projection = resolveProjection(group, options);
  if (projection.notice) notices.push(projection.notice);
  if (projection.assumed) notices.push(`${group.name}: CRS asignado manualmente como ${projection.sourceCrs}.`);

  const decoded = await decodeRecords(group, shp, encoding?.encoding ?? 'windows-1252');
  if (decoded.length !== structure.records) throw new ShapefileImportError(`${group.name}: se leyeron ${decoded.length} de ${structure.records} registros del .shp; el archivo parece dañado.`);
  const geographic = projection.definition === 'EPSG:4326' || projection.sourceCrs === 'EPSG:4326';
  // Web Mercator sources are already in the map CRS: kept bit-for-bit instead of a proj4 round trip.
  const converter = projection.definition === 'EPSG:3857' ? null : proj4(projection.definition, 'EPSG:3857');
  const forward = (coordinate: [number, number]) => (converter ? converter.forward(coordinate) : coordinate) as [number, number];
  const layerId = `layer:shp:${slug(group.name)}:${++importSequence}`;
  const format = new GeoJSON();
  const rejected: { record: number; reason: string }[] = []; const nulls: number[] = [];
  const records: VectorFeatureRecord[] = [];
  decoded.forEach(({ geometry, properties }, index) => {
    const record = index + 1;
    if (!geometry) { nulls.push(record); return; }
    const problem = geometryProblem(geometry);
    if (problem) { rejected.push({ record, reason: problem }); return; }
    if (geographic && outOfGeographicRange(geometry)) throw new ShapefileImportError(`${group.name}: las coordenadas no son longitud/latitud válidas para ${projection.sourceCrs} (registro ${record}). Verifica el CRS de origen.`);
    const feature = format.readFeature({ type: 'Feature', geometry: transformGeometry(geometry, forward), properties: null }) as Feature<Geometry>;
    const attributes: Record<string, AttributeValue> = {};
    Object.entries(properties).forEach(([key, value]) => { attributes[key] = normalizeValue(value); });
    // The id keeps the original record index (0-based), so «Registro N» stays traceable to the .shp.
    const id = `${layerId}/feature:${index}`;
    feature.set('layerId', layerId, true);
    feature.set('genericFeatureId', id, true);
    records.push({ id, feature, attributes: attributes satisfies OriginalAttributes });
  });
  if (!records.length) throw new ShapefileImportError(`${group.name}: no contiene geometrías utilizables (${structure.records} registro(s): ${nulls.length} nulo(s), ${rejected.length} rechazado(s)).`);
  if (nulls.length) notices.push(`${group.name}: ${nulls.length} registro(s) sin geometría (NullShape: ${listRecords(nulls)}); no se representan en el mapa.`);
  if (rejected.length) notices.push(`${group.name}: ${rejected.length} registro(s) con geometría no válida no se incorporaron (${rejected.slice(0, 12).map(({ record, reason }) => `${record}: ${reason}`).join('; ')}${rejected.length > 12 ? '; …' : ''}).`);
  const layer = createGenericLayer({ id: layerId, name: group.name, sourceName: group.sourceName, sourceFormat: 'SHP', sourceCrs: projection.sourceCrs, assumedCrs: projection.assumed, records, warnings: notices });
  return {
    layer,
    summary: {
      key: group.key, name: group.name, sourceName: group.sourceName, shapeType: shapeInfo.name, geometryLabel: shapeInfo.label,
      recordCount: structure.records, validCount: records.length, nullCount: nulls.length, rejectedCount: rejected.length,
      sourceCrs: projection.sourceCrs, crsOrigin: projection.assumed ? 'manual' : 'prj',
      hasDbf: Boolean(dbf), fieldCount: layer.attributeFields.length, encoding: encoding?.encoding ?? null, hasShx: Boolean(group.components.shx),
      shxStatus: !group.components.shx ? 'missing' : shxIssues?.length ? 'inconsistent' : 'valid', warnings: notices,
    },
  };
}

/**
 * Reads and validates every dataset of the selection before anything is added: one failing dataset fails the batch.
 * Datasets without a usable CRS are reported together (one entry each) so the user confirms each one explicitly.
 */
export async function inspectShapefiles(files: readonly File[], options: ShapefileImportOptions = {}): Promise<ShapefileInspection> {
  const { groups, notices } = await collectGroups(files);
  const pending: CrsPendingDataset[] = [];
  groups.forEach((group) => {
    try { resolveProjection(group, options); } catch (error) {
      if (error instanceof ShapefileCrsRequiredError) pending.push(...error.datasets);
      else throw error;
    }
  });
  if (pending.length) throw new ShapefileCrsRequiredError(pending);
  const datasets = await Promise.all(groups.map((group) => parseGroup(group, options)));
  return { datasets, notices };
}

export async function importShapefiles(files: readonly File[], options: ShapefileImportOptions = {}): Promise<VectorLayerModel[]> {
  return (await inspectShapefiles(files, options)).datasets.map(({ layer }) => layer);
}
